/**
 * CertificateListPage — Issue #362.
 *
 * Lists HR credentials with masked sensitive fields and surfaces the warning
 * candidates + freeze state. Detail / create / review live behind dedicated
 * routes because each touches a sensitive lifecycle action.
 *
 * 红线 (AI GUIDE §6):
 *   - 仅展示 last4 + 状态；不渲染 cipher / hash。
 *   - 列表与预警候选都走 generated api-client；不构造假数据。
 *   - 错误以 Card 形式展示；403 / 409 / 404 走统一文案。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_CERTIFICATE_REVIEW_STATUS_LABELS,
  HR_CERTIFICATE_STATUS_LABELS,
  HR_CERTIFICATE_WARNING_BUCKET_LABELS,
  maskCertificateNo,
  useHrCertificates,
  useHrCertificateWarnings,
  type HrCertificateDto,
} from '@/hooks/useHrCertificates';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('zh-CN');
}

export default function CertificateListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.CERTIFICATE_READ);
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.CERTIFICATE_WRITE);

  const { items, total, loading, error, fetchCertificates } =
    useHrCertificates();
  const {
    items: warnings,
    generatedAt,
    fetchWarnings,
  } = useHrCertificateWarnings();

  const [statusFilter, setStatusFilter] = useState<
    HrCertificateDto['status'] | ''
  >('');
  const [reviewFilter, setReviewFilter] = useState<
    HrCertificateDto['reviewStatus'] | ''
  >('');
  const [showOnlyFrozen, setShowOnlyFrozen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchCertificates({
      status: statusFilter || undefined,
      reviewStatus: reviewFilter || undefined,
      loginFreezeActive: showOnlyFrozen ? true : undefined,
      page,
      limit,
    });
    void fetchWarnings();
  }, [
    canRead,
    statusFilter,
    reviewFilter,
    showOnlyFrozen,
    page,
    fetchCertificates,
    fetchWarnings,
  ]);

  const warningById = useMemo(() => {
    const map = new Map<string, (typeof warnings)[number]>();
    for (const w of warnings) map.set(w.id, w);
    return map;
  }, [warnings]);

  if (!canRead) return <AccessDenied resource="资质证照" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">资质证照管理</h1>
          <p className="text-sm text-slate-500">
            资质证照到期前自动预警 (30 / 7 天)；
            必备证照超期且未续期审批通过时冻结对应账号的内部后台登录。
          </p>
        </div>
        {canWrite ? (
          <Link
            href="/hr/certificates/create"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
          >
            新建证照 →
          </Link>
        ) : null}
      </header>

      <Card className="border-amber-200 bg-amber-50/40 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-amber-900">
              当前预警候选 {warnings.length} 条
            </div>
            <div className="text-xs text-amber-700">
              快照时间 {generatedAt ? new Date(generatedAt).toLocaleString('zh-CN') : '—'}
            </div>
          </div>
          {warnings.length > 0 ? (
            <ul className="space-y-1 text-xs text-amber-900">
              {warnings.slice(0, 5).map((w) => (
                <li key={w.id}>
                  <Link
                    href={`/hr/certificates/${w.id}`}
                    className="underline hover:text-amber-700"
                  >
                    {w.id.slice(0, 8)}
                  </Link>
                  {' · '}
                  {HR_CERTIFICATE_WARNING_BUCKET_LABELS[w.bucket]}
                  {' · 剩余 '}
                  {w.daysUntilExpiry}
                  {' 天'}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-xs text-amber-700">最近 30 天无新增预警</div>
          )}
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          证照状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                (e.target.value as HrCertificateDto['status']) || '',
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(
              Object.entries(HR_CERTIFICATE_STATUS_LABELS) as [
                HrCertificateDto['status'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          审核状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={reviewFilter}
            onChange={(e) => {
              setReviewFilter(
                (e.target.value as HrCertificateDto['reviewStatus']) || '',
              );
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(
              Object.entries(HR_CERTIFICATE_REVIEW_STATUS_LABELS) as [
                HrCertificateDto['reviewStatus'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showOnlyFrozen}
            onChange={(e) => {
              setShowOnlyFrozen(e.target.checked);
              setPage(1);
            }}
          />
          仅显示已冻结登录的证照
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full table-fixed text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="w-32 px-3 py-2">员工 ID</th>
              <th className="w-32 px-3 py-2">证书类型</th>
              <th className="w-28 px-3 py-2">证书号后四位</th>
              <th className="w-28 px-3 py-2">到期日</th>
              <th className="w-28 px-3 py-2">证照状态</th>
              <th className="w-28 px-3 py-2">审核状态</th>
              <th className="w-24 px-3 py-2 text-center">必备</th>
              <th className="w-24 px-3 py-2 text-center">冻结登录</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const warning = warningById.get(row.id);
              return (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.employeeId}
                  </td>
                  <td className="px-3 py-2">{row.certificateType}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {maskCertificateNo(row.certificateNoLast4)}
                  </td>
                  <td className="px-3 py-2">
                    {fmtDate(row.expiresAt)}
                    {warning ? (
                      <span className="ml-2 rounded bg-amber-100 px-1 text-xs text-amber-800">
                        {HR_CERTIFICATE_WARNING_BUCKET_LABELS[warning.bucket]}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    {HR_CERTIFICATE_STATUS_LABELS[row.status]}
                  </td>
                  <td className="px-3 py-2">
                    {HR_CERTIFICATE_REVIEW_STATUS_LABELS[row.reviewStatus]}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.mandatory ? '是' : '否'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.loginFreezeActive ? (
                      <span className="rounded bg-red-100 px-1 text-xs text-red-700">
                        已冻结
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      href={`/hr/certificates/${row.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      查看详情
                    </Link>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-3 py-6 text-center text-slate-400"
                >
                  暂无符合条件的证照记录
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
